/**
 * Hook único para el catálogo de líneas WhatsApp del tenant.
 *
 * - Carga todas las columnas necesarias (lista, sidebar nav, conexión).
 * - Hace un segundo fetch para traer `inboxes.setup_status` por id (no embed,
 *   para tolerar bases antiguas donde la columna o la FK aún no exista).
 * - Usa TanStack Query (staleTime 30s) + useWaRealtimeSub para invalidar el
 *   cache en INSERT/UPDATE/DELETE sobre wa_phone_lines e inboxes.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useWaRealtimeSub } from '@/hooks/useWaRealtimeSub';

export interface WaPhoneLineRow {
  id: string;
  display_id: number;
  label: string;
  phone_number: string;
  provider: string;
  is_default: boolean;
  is_active: boolean;
  connection_status: string;
  profile_pic_url: string | null;
  business_name: string | null;
  created_at: string;
  sort_order: number;
  auto_reopen_on_customer_reply?: boolean | null;
  evolution_api_url?: string | null;
  evolution_api_key?: string | null;
  evolution_instance_name?: string | null;
  inbox_id?: string | null;
  /** Resuelto vía segundo fetch a `inboxes.setup_status`. */
  inbox_setup_status?: 'draft' | 'active' | 'archived' | null;
}

interface Options {
  /** Si true, solo retorna líneas con `is_active=true`. */
  onlyActive?: boolean;
}

const BASE_COLS =
  'id, display_id, label, phone_number, provider, is_default, is_active, connection_status, profile_pic_url, business_name, created_at, sort_order, auto_reopen_on_customer_reply, evolution_api_url, evolution_api_key, evolution_instance_name';
const BASE_COLS_WITH_INBOX = `${BASE_COLS}, inbox_id`;

async function fetchPhoneLines(tenantId: string, onlyActive?: boolean): Promise<WaPhoneLineRow[]> {
  // 1) Query base. Orden: sort_order (manual del usuario) → created_at como
  //    tiebreaker. is_default ya no fuerza primer puesto: si el usuario quiere
  //    su default arriba lo arrastra. Esto matchea expectativa de UX
  //    moderna (Notion/Linear) — el orden lo controla el usuario, no flags
  //    implícitos.
  const { data: initialData, error } = await supabase
    .from('wa_phone_lines')
    .select(BASE_COLS_WITH_INBOX)
    .eq('tenant_id', tenantId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  // Anotado explícito: la query base y el retry devuelven shapes distintos
  // (con/sin `inbox_id`) — WaPhoneLineRow los cubre a ambos porque `inbox_id`
  // es opcional. Sin la anotación, TS fija el shape del primer select y el
  // retry no asigna.
  let data: WaPhoneLineRow[] | null = initialData;
  if (error) {
    const retry = await supabase
      .from('wa_phone_lines')
      .select(BASE_COLS)
      .eq('tenant_id', tenantId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    data = retry.data;
  }

  let rows = data ?? [];
  if (onlyActive) rows = rows.filter((r) => r.is_active);

  // 2) Segundo fetch: setup_status por inbox_id (best-effort)
  const inboxIds = rows.map((r) => r.inbox_id).filter((x): x is string => !!x);
  if (inboxIds.length > 0) {
    const { data: inboxRows } = await supabase
      .from('inboxes')
      .select('id, setup_status')
      .in('id', inboxIds);
    const statusById = new Map<string, WaPhoneLineRow['inbox_setup_status']>();
    for (const r of (inboxRows ?? []) as Array<{ id: string; setup_status: WaPhoneLineRow['inbox_setup_status'] }>) {
      statusById.set(r.id, r.setup_status);
    }
    rows = rows.map((r) => ({
      ...r,
      inbox_setup_status: r.inbox_id ? statusById.get(r.inbox_id) ?? null : null,
    }));
  }

  return rows;
}

export function useWaPhoneLines(tenantId: string | null | undefined, opts: Options = {}) {
  const queryClient = useQueryClient();
  const queryKey = ['wa-phone-lines', tenantId, opts.onlyActive] as const;
  /*
   * 🪤 SE INVALIDA EL PREFIJO, NO LA CLAVE EXACTA. Cuando una línea cambia, TODAS las vistas de
   * líneas quedan viejas — la que pidió solo las activas y la que las pidió todas. Invalidar la
   * clave propia dejaría a la otra mostrando el nombre anterior, que es la mitad del problema que
   * el founder reportó al renombrar «Shalom» a «Nutrizen».
   */
  const prefijoDeLineas = ['wa-phone-lines', tenantId] as const;

  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: () => fetchPhoneLines(tenantId!, opts.onlyActive),
    enabled: !!tenantId,
    staleTime: 0, // 🔧 SIEMPRE refetch (no confiar en caché obsoleto)
    gcTime: 5 * 60 * 1000, // Pero guarda en memory 5 min por si vuelves a la misma página
  });

  useWaRealtimeSub({
    channelKey: `wa-phone-lines-${tenantId ?? ''}`,
    tenantId,
    subscriptions: [
      {
        table: 'wa_phone_lines',
        filter: `tenant_id=eq.${tenantId}`,
        event: '*',
        onData: () => { void queryClient.invalidateQueries({ queryKey: prefijoDeLineas }); },
      },
      {
        table: 'inboxes',
        filter: `tenant_id=eq.${tenantId}`,
        event: 'UPDATE',
        onData: () => { void queryClient.invalidateQueries({ queryKey: prefijoDeLineas }); },
      },
      {
        table: 'inboxes',
        filter: `tenant_id=eq.${tenantId}`,
        event: 'INSERT',
        onData: () => { void queryClient.invalidateQueries({ queryKey: prefijoDeLineas }); },
      },
    ],
  });

  return { lines: data ?? [], loading: isLoading, refetch };
}
